import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, icons } from '../../ui.js';
import { useRecorder, recordingSupported, type Recording } from './useRecorder.js';

/** Press and hold to talk; slide away to throw it away.
 *
 *  The gesture is one continuous thing — down, drag, up — so it is driven by
 *  pointer events with capture, which keeps delivering to this button even after
 *  the finger has left it. `touch-action` is off on the button permanently
 *  rather than switched on the way down: it cannot be changed once a gesture has
 *  begun, so it has to be right before the finger lands.
 *
 *  There is no buzz when recording starts. iOS gives web pages no vibration at
 *  all, so the confirmation has to be something you can see. */

const CANCEL_AT = 90;   // px of slide before letting go means "forget it"

export function HoldToTalk({ onRecorded, disabled, variant = 'floating' }: {
  onRecorded: (r: Recording) => void;
  disabled?: boolean;
  /** `floating` rides above the + on a phone; `inline` sits beside the New task
   *  button in the sidebar, where there is no + to ride above. */
  variant?: 'floating' | 'inline';
}) {
  const { phase, seconds, levels, start, stop, cancel } = useRecorder();
  const [slide, setSlide] = useState(0);
  const origin = useRef(0);
  const active = useRef(false);
  const supported = recordingSupported();

  const armed = phase === 'recording' || phase === 'asking';
  const willCancel = slide >= CANCEL_AT;

  const finish = useCallback(async (discard: boolean) => {
    if (!active.current) return;
    active.current = false;
    setSlide(0);
    if (discard) { cancel(); return; }
    const rec = await stop();
    // Under a third of a second is a mis-tap, not a message.
    if (rec && rec.seconds >= 0.4) onRecorded(rec);
  }, [cancel, stop, onRecorded]);

  const down = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled || !supported) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    origin.current = e.clientX;
    active.current = true;
    setSlide(0);
    void start();
  };

  const move = (e: React.PointerEvent) => {
    if (!active.current) return;
    setSlide(Math.max(0, origin.current - e.clientX));
  };

  const up = () => { void finish(willCancel); };

  // Leaving the app mid-sentence loses the recording anyway — iOS suspends us —
  // so end it deliberately instead of coming back to a half-thing.
  useEffect(() => {
    const away = () => { if (document.visibilityState === 'hidden' && active.current) void finish(true); };
    document.addEventListener('visibilitychange', away);
    return () => document.removeEventListener('visibilitychange', away);
  }, [finish]);

  if (!supported) return null;

  return (
    <>
      {armed && (
        <div className="ptt-sheet" role="status" aria-live="polite">
          <div className="ptt-row">
            <span className={`ptt-dot${willCancel ? ' off' : ''}`} />
            <span className="ptt-time">{fmt(seconds)}</span>
            <div className="ptt-wave" aria-hidden>
              {levels.map((l, i) => (
                <span key={i} style={{ height: `${6 + l * 42}px`, opacity: willCancel ? .3 : .9 }} />
              ))}
            </div>
          </div>
          <div className={`ptt-hint${willCancel ? ' armed' : ''}`}>
            {willCancel ? 'Let go to forget it' : (
              <><Icon path={icons.chevronLeft} size={14} /> Slide away to cancel</>
            )}
          </div>
        </div>
      )}

      <button
        className={`ptt ptt-${variant}${armed ? ' on' : ''}${willCancel ? ' cancelling' : ''}`}
        aria-label="Hold to say a task"
        disabled={disabled}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={() => void finish(true)}
        onContextMenu={(e) => e.preventDefault()}
        style={{ transform: armed ? `translateX(${-Math.min(slide, CANCEL_AT)}px)` : undefined }}
      >
        <Icon path={icons.mic} size={armed ? 25 : 21} w={1.9} />
      </button>
    </>
  );
}

const fmt = (s: number) => {
  const t = Math.floor(s);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};
